/**
 * Loading Dialog Component
 *
 * Non-dismissible modal dialog that displays a progress bar for long-running operations.
 * Features random progress simulation up to 90%, "taking longer" message, and jumps to 100% on completion.
 *
 * @module components/ui/LoadingDialog
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@/hooks/useTheme';
import { formatEstimatedTime } from '@/utils/mealPlanEstimation';

/**
 * Props for LoadingDialog component
 */
export interface LoadingDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Title text to display */
  title?: string;
  /** Message text to display */
  message?: string;
  /** Estimated time in seconds (for display only) */
  estimatedTimeSeconds?: number;
  /** Whether the operation is complete (triggers 100% progress) */
  isComplete?: boolean;
}

/**
 * Loading Dialog Component
 *
 * Displays a non-dismissible loading dialog with animated progress bar.
 * Progress randomly increments up to 90%, then jumps to 100% when complete.
 */
export const LoadingDialog: React.FC<LoadingDialogProps> = ({
  isOpen,
  title = 'Processing...',
  message = 'Please wait while we process your request.',
  estimatedTimeSeconds = 30,
  isComplete = false,
}) => {
  const { isDark } = useTheme();
  const [progress, setProgress] = useState(0);
  const [messageStage, setMessageStage] = useState<'none' | 'working' | 'longer'>('none');

  // Reset progress when dialog opens
  useEffect(() => {
    if (isOpen && !isComplete) {
      setProgress(0);
      setMessageStage('none');
    }
  }, [isOpen, isComplete]);

  // Simulate progress up to 90%
  useEffect(() => {
    if (!isOpen || isComplete) return;

    const interval = setInterval(() => {
      setProgress((prev) => {
        // Random increment between 1-5%
        const increment = Math.random() * 4 + 1;
        const newProgress = Math.min(prev + increment, 90);
        return newProgress;
      });
    }, Math.max((estimatedTimeSeconds * 1000) / 20, 1000)); // Update at least every second

    return () => clearInterval(interval);
  }, [isOpen, isComplete]);

  // Show progressive messages based on elapsed time
  useEffect(() => {
    if (!isOpen || isComplete) return;

    // First message: "Still working on it..." after estimatedTimeSeconds
    const firstTimeout = setTimeout(() => {
      setMessageStage('working');
    }, estimatedTimeSeconds * 1000);

    // Second message: "This is taking a bit longer..." 2 minutes after estimatedTimeSeconds
    const secondTimeout = setTimeout(() => {
      setMessageStage('longer');
    }, (estimatedTimeSeconds + 120) * 1000);

    return () => {
      clearTimeout(firstTimeout);
      clearTimeout(secondTimeout);
    };
  }, [isOpen, isComplete, estimatedTimeSeconds]);

  // Jump to 100% when complete
  useEffect(() => {
    if (isComplete) {
      setProgress(100);
    }
  }, [isComplete]);

  // Prevent body scroll when dialog is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="loading-dialog-title"
      aria-describedby="loading-dialog-message"
      style={{
        position: 'fixed',
        top: '0px',
        left: '0px',
        width: '100vw',
        height: '100vh',
        backgroundColor: isDark ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.5)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        style={{
          backgroundColor: isDark ? '#1f2937' : 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '400px',
          padding: '32px',
          boxShadow: isDark
            ? '0 20px 25px -5px rgba(0, 0, 0, 0.4)'
            : '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          border: isDark ? '1px solid #374151' : 'none',
        }}
      >
        {/* Title */}
        <h2
          id="loading-dialog-title"
          style={{
            fontSize: '20px',
            fontWeight: '600',
            color: isDark ? '#f9fafb' : '#111827',
            margin: '0 0 16px 0',
            textAlign: 'center',
          }}
        >
          {title}
        </h2>

        {/* Message */}
        <p
          id="loading-dialog-message"
          style={{
            fontSize: '14px',
            color: isDark ? '#d1d5db' : '#6b7280',
            margin: '0 0 24px 0',
            textAlign: 'center',
            lineHeight: '1.5',
          }}
        >
          {message}
        </p>

        {/* Progress Bar */}
        <div style={{ marginBottom: '16px' }}>
          <div
            style={{
              width: '100%',
              height: '8px',
              backgroundColor: isDark ? '#374151' : '#e5e7eb',
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${progress}%`,
                height: '100%',
                backgroundColor: '#3b82f6',
                borderRadius: '4px',
                transition: 'width 0.5s ease-out',
              }}
              role="progressbar"
              aria-valuenow={Math.round(progress)}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '8px',
            }}
          >
            <span
              style={{
                fontSize: '12px',
                color: isDark ? '#9ca3af' : '#6b7280',
              }}
            >
              {Math.round(progress)}%
            </span>
            <span
              style={{
                fontSize: '12px',
                color: isDark ? '#9ca3af' : '#6b7280',
              }}
            >
              ~{formatEstimatedTime(estimatedTimeSeconds)}
            </span>
          </div>
        </div>

        {/* Progressive status messages */}
        {messageStage !== 'none' && !isComplete && (
          <div
            style={{
              padding: '12px',
              borderRadius: '8px',
              backgroundColor: isDark ? '#374151' : '#f3f4f6',
              marginTop: '16px',
            }}
          >
            <p
              style={{
                fontSize: '13px',
                color: isDark ? '#d1d5db' : '#6b7280',
                margin: 0,
                textAlign: 'center',
              }}
            >
              {messageStage === 'working' && 'Still working on it...'}
              {messageStage === 'longer' && 'This is taking a bit longer than expected. Please wait some more...'}
            </p>
          </div>
        )}

        {/* Spinner */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '24px',
          }}
        >
          <svg
            style={{
              width: '32px',
              height: '32px',
              animation: 'spin 1s linear infinite',
              color: '#3b82f6',
            }}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              style={{ opacity: 0.25 }}
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              style={{ opacity: 0.75 }}
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>

        {/* Add keyframes for spinner animation */}
        <style>
          {`
            @keyframes spin {
              from {
                transform: rotate(0deg);
              }
              to {
                transform: rotate(360deg);
              }
            }
          `}
        </style>
      </div>
    </div>,
    document.body
  );
};

export default LoadingDialog;